import { type JSXElement, type ParentComponent, createContext, createEffect, createSignal, useContext } from "solid-js";
import { createStore } from "solid-js/store";
import { isServer } from "solid-js/web";

// Navigation pane state machine
export const NavigationPaneMode = {
  COLLAPSED: "collapsed", // Not expanded, allows auto-expand
  UNDOCKED: "undocked", // Auto-expanded due to hover, floating panel
  DOCKED: "docked", // User clicked to dock, integrated sidebar
} as const;

export type NavigationPaneMode = (typeof NavigationPaneMode)[keyof typeof NavigationPaneMode];

// Chat state machine for prompt submission and layout transitions
export const ChatLayoutState = {
  INITIAL: "initial", // Pre-prompt state with centered layout // initial
  ACTIVE: "active", // Post-prompt state with active chat layout
  STREAMING: "streaming", // Currently receiving AI response
  ERROR: "error", // Error state requiring user intervention
} as const;

export type ChatLayoutState = (typeof ChatLayoutState)[keyof typeof ChatLayoutState];

// Extended pane state machine with enhanced modes
export const ExtendedPaneMode = {
  HIDDEN: "hidden", // Panel is not visible
  PREVIEW: "preview", // Panel is visible but collapsed/minimized
  ACTIVE: "active", // Panel is fully expanded and interactive
  FLOATING: "floating", // Panel is detached from main layout
} as const;

export type ExtendedPaneMode = (typeof ExtendedPaneMode)[keyof typeof ExtendedPaneMode];

// Prompt bar mode state machine
export const PromptBarMode = {
  NORMAL: "normal", // Standard prompt input mode
  DROPZONE: "dropzone", // File upload dropzone mode
  KNOWLEDGE_MENU: "knowledge_menu", // Knowledge sources menu open
} as const;

export type PromptBarMode = (typeof PromptBarMode)[keyof typeof PromptBarMode];

// Interaction mode for the prompt bar
export const InteractionMode = {
  CHAT: "chat", // Standard chat interaction
  AGENT: "agent", // Agent-based interaction
  WORKFLOW: "workflow", // Workflow automation
} as const;

export type InteractionMode = (typeof InteractionMode)[keyof typeof InteractionMode];

export interface NavigationPaneState {
  mode: NavigationPaneMode;
  width: number;
  secondaryPane: "assets" | "concepts" | null;
}

// Computed helpers
export const isExpanded = (state: NavigationPaneState): boolean =>
  state.mode === NavigationPaneMode.UNDOCKED || state.mode === NavigationPaneMode.DOCKED;

export const allowsAutoExpand = (state: NavigationPaneState): boolean =>
  state.mode === NavigationPaneMode.COLLAPSED || state.mode === NavigationPaneMode.UNDOCKED;

// Chat state helpers
export const isPromptSubmitted = (state: ChatState): boolean => state.layoutState !== ChatLayoutState.INITIAL;

export const isStreamingEnabled = (state: ChatState): boolean => state.layoutState === ChatLayoutState.STREAMING;

export const canAcceptNewPrompt = (state: ChatState): boolean =>
  state.layoutState === ChatLayoutState.ACTIVE || state.layoutState === ChatLayoutState.ERROR;

// Extended pane helpers
export const isExtendedPaneVisible = (state: ExtendedPaneState): boolean => state.mode !== ExtendedPaneMode.HIDDEN;

export const isExtendedPaneInteractive = (state: ExtendedPaneState): boolean =>
  state.mode === ExtendedPaneMode.ACTIVE || state.mode === ExtendedPaneMode.FLOATING;

export interface ChatState {
  layoutState: ChatLayoutState;
  isStreamingActive: boolean;
  messageCount: number;
}

export interface PromptBarState {
  mode: PromptBarMode;
  interactionMode: InteractionMode;
  knowledgeSources: {
    training: boolean;
    web: boolean;
    myKnowledge: boolean;
    memories: boolean;
  };
}

export const ExtendedPaneContentType = {
  ARTIFACT: "artifact",
  INTERSTITIAL: "interstitial",
  INTERSTITIAL_GENERATING: "interstitial_generating",
} as const;

export type ExtendedPaneContentType = (typeof ExtendedPaneContentType)[keyof typeof ExtendedPaneContentType];

export interface ExtendedPaneContentData {
  operationId: string;
  title?: string;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  chat: any; // ReturnType<typeof createStreamingChat>
  type: ExtendedPaneContentType;
}

// Legacy interface for backwards compatibility - now includes type
export interface ArtifactData {
  operationId: string;
  blockId?: string; // Optional block ID for block-specific content
  title?: string;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  chat: any; // ReturnType<typeof createStreamingChat>
  type?: ExtendedPaneContentType; // Type field for routing to correct viewer
}

export interface ExtendedPaneState {
  mode: ExtendedPaneMode;
  position: "left" | "right";
  width: number;
  hasBeenManuallyResized: boolean;
  content?: JSXElement; // Content component to render in the pane
  // Single active artifact instead of multiple tabs
  activeArtifact?: ArtifactData;
}

export interface AppUIState {
  navigationPane: NavigationPaneState;
  extendedPane: ExtendedPaneState;
  chat: ChatState;
  promptBar: PromptBarState;
  isDebugMode: boolean;
  useRichTextReplacement: boolean;
  enableAutoCollapseOperations: boolean;
}

export interface AppUIContextType {
  state: AppUIState;
  isHydrated: () => boolean;
  actions: {
    // Navigation pane actions
    toggleNavigationPane: () => void;
    setNavigationPaneMode: (mode: NavigationPaneMode) => void;
    setNavigationPaneWidth: (width: number) => void;

    openSecondaryPane: (pane: "assets" | "concepts") => void;
    closeSecondaryPane: () => void;

    // Chat state actions
    submitPrompt: () => void;
    startStreaming: () => void;
    stopStreaming: () => void;
    setChatError: () => void;
    resetChat: () => void;
    incrementMessageCount: () => void;

    // Extended pane actions
    setExtendedPaneMode: (mode: ExtendedPaneMode) => void;
    setExtendedPanePosition: (position: "left" | "right") => void;
    setExtendedPaneWidth: (width: number, isManualResize?: boolean) => void;
    setExtendedPaneContent: (content: JSXElement) => void;
    clearExtendedPaneContent: () => void;
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    setExtendedPaneArtifact: (operationId: string, chat: any, title?: string, blockId?: string) => void;
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    setExtendedPaneInterstitial: (operationId: string, chat: any, title?: string, blockId?: string) => void;
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    setExtendedPaneInterstitialGenerating: (operationId: string, chat: any, title?: string, blockId?: string) => void;
    clearExtendedPaneArtifact: () => void;
    toggleExtendedPane: () => void;

    // Prompt bar actions
    setPromptBarMode: (mode: PromptBarMode) => void;
    setInteractionMode: (mode: InteractionMode) => void;
    toggleKnowledgeSource: (source: keyof PromptBarState["knowledgeSources"]) => void;
    resetPromptBar: () => void;

    // Debug actions
    toggleDebugMode: () => void;
    toggleRichTextReplacement: () => void;
    toggleAutoCollapseOperations: () => void;
  };
}

const AppUIContext = createContext<AppUIContextType>();

const DEFAULT_STATE: AppUIState = {
  navigationPane: {
    mode: NavigationPaneMode.UNDOCKED,
    width: 300,
    secondaryPane: null,
  },
  extendedPane: {
    mode: ExtendedPaneMode.HIDDEN,
    position: "right",
    width: 300,
    hasBeenManuallyResized: false,
    content: undefined,
    activeArtifact: undefined,
  },
  chat: {
    layoutState: ChatLayoutState.INITIAL,
    isStreamingActive: false,
    messageCount: 0,
  },
  promptBar: {
    mode: PromptBarMode.NORMAL,
    interactionMode: InteractionMode.CHAT,
    knowledgeSources: {
      training: true,
      web: true,
      myKnowledge: true,
      memories: true,
    },
  },
  isDebugMode: false,
  useRichTextReplacement: true, // Default to using rich text replacement
  enableAutoCollapseOperations: true, // Default to enabling auto-collapse
};

// Cookie key for persistence
const UI_STATE_COOKIE_KEY = "storytell-ui-state";

// Helper functions for cookie management
const getCookieValue = (key: string): string | null => {
  if (isServer) return null;
  const match = document.cookie.match(new RegExp(`(^| )${key}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
};

const setCookieValue = (key: string, value: string): void => {
  if (isServer) return;
  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 1); // 1 year expiry
  document.cookie = `${key}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/; SameSite=Strict`;
};

const loadStateFromCookie = (): Partial<AppUIState> => {
  const cookieValue = getCookieValue(UI_STATE_COOKIE_KEY);
  if (!cookieValue) return {};

  try {
    return JSON.parse(cookieValue);
  } catch {
    return {};
  }
};

const saveStateToCookie = (state: AppUIState): void => {
  // Only persist certain state properties, not extended pane mode or chat state
  const persistedState = {
    navigationPane: state.navigationPane,
    extendedPane: {
      position: state.extendedPane.position,
      width: state.extendedPane.width,
      hasBeenManuallyResized: state.extendedPane.hasBeenManuallyResized,
      // Exclude mode from persistence - always start hidden
    },
    promptBar: {
      knowledgeSources: state.promptBar.knowledgeSources,
      // Don't persist mode or interactionMode - always start fresh
    },
    // Persist debug flags
    isDebugMode: state.isDebugMode,
    useRichTextReplacement: state.useRichTextReplacement,
    enableAutoCollapseOperations: state.enableAutoCollapseOperations,
    // Don't persist chat state - always start fresh
  };
  setCookieValue(UI_STATE_COOKIE_KEY, JSON.stringify(persistedState));
};

export const AppUIContextProvider: ParentComponent = (props) => {
  // Always start with default state for SSR consistency
  const [state, setState] = createStore<AppUIState>(DEFAULT_STATE);
  const [isHydrated, setIsHydrated] = createSignal(false);

  // Load from cookies only after hydration
  createEffect(() => {
    if (!isServer && !isHydrated()) {
      const savedState = loadStateFromCookie();
      if (Object.keys(savedState).length > 0) {
        // Merge saved state but ensure extended pane mode starts as HIDDEN and chat state starts fresh
        const { extendedPane: savedExtendedPane, ...otherSavedState } = savedState;

        // Clean state - ensure extended pane always starts hidden and chat starts fresh
        const cleanExtendedPane = savedExtendedPane
          ? {
              mode: ExtendedPaneMode.HIDDEN, // Always start hidden
              position: savedExtendedPane.position || DEFAULT_STATE.extendedPane.position,
              width: savedExtendedPane.width || DEFAULT_STATE.extendedPane.width,
              hasBeenManuallyResized: savedExtendedPane.hasBeenManuallyResized || false,
              content: undefined, // Always start with no content
              activeArtifact: undefined, // Always start with no active artifact
            }
          : DEFAULT_STATE.extendedPane;

        // Clean prompt bar state - preserve knowledge sources but reset mode and interaction
        const cleanPromptBar = otherSavedState.promptBar
          ? {
              mode: PromptBarMode.NORMAL, // Always start with normal mode
              interactionMode: InteractionMode.CHAT, // Always start with chat mode
              knowledgeSources: {
                training: otherSavedState.promptBar.knowledgeSources?.training ?? true,
                web: otherSavedState.promptBar.knowledgeSources?.web ?? true,
                myKnowledge: otherSavedState.promptBar.knowledgeSources?.myKnowledge ?? true,
                memories: otherSavedState.promptBar.knowledgeSources?.memories ?? true,
              },
            }
          : DEFAULT_STATE.promptBar;

        const cleanState = {
          navigationPane: otherSavedState.navigationPane || DEFAULT_STATE.navigationPane,
          extendedPane: cleanExtendedPane,
          chat: DEFAULT_STATE.chat, // Always start with fresh chat state
          promptBar: cleanPromptBar, // Preserve knowledge sources, reset modes
          isDebugMode: otherSavedState.isDebugMode ?? DEFAULT_STATE.isDebugMode, // Restore debug mode
          useRichTextReplacement: otherSavedState.useRichTextReplacement ?? DEFAULT_STATE.useRichTextReplacement, // Restore rich text flag
          enableAutoCollapseOperations:
            otherSavedState.enableAutoCollapseOperations ?? DEFAULT_STATE.enableAutoCollapseOperations, // Restore auto-collapse flag
        };

        setState(cleanState);

        // Immediately save the cleaned state to update the cookie
        saveStateToCookie(cleanState);
      }
      setIsHydrated(true);
    }
  });

  // Save state to cookies whenever it changes (but only after hydration)
  createEffect(() => {
    if (!isServer && isHydrated()) {
      saveStateToCookie(state);
    }
  });

  const actions: AppUIContextType["actions"] = {
    // Navigation pane actions
    toggleNavigationPane: () => {
      const currentMode = state.navigationPane.mode;

      if (currentMode === NavigationPaneMode.COLLAPSED) {
        // From collapsed -> docked
        setState("navigationPane", "mode", NavigationPaneMode.DOCKED);
      } else if (currentMode === NavigationPaneMode.UNDOCKED) {
        // From undocked -> docked
        setState("navigationPane", "mode", NavigationPaneMode.DOCKED);
      } else if (currentMode === NavigationPaneMode.DOCKED) {
        // From docked -> undocked
        // This allows the user to "undock" the nav while keeping it visible
        setState("navigationPane", "mode", NavigationPaneMode.UNDOCKED);
      }
    },

    setNavigationPaneMode: (mode: NavigationPaneMode) => {
      setState("navigationPane", "mode", mode);
      if (mode !== NavigationPaneMode.DOCKED) {
        setState("navigationPane", "secondaryPane", null);
      }
    },

    setNavigationPaneWidth: (width: number) => {
      // Spec: up to 50% of viewport width, but never less than 300px
      if (isServer) return;
      const maxWidth = Math.max(300, window.innerWidth * 0.5);
      setState("navigationPane", "width", Math.max(300, Math.min(maxWidth, width)));
    },

    // Chat state actions
    submitPrompt: () => {
      setState("chat", {
        layoutState: ChatLayoutState.ACTIVE,
        messageCount: state.chat.messageCount + 1,
      });
    },

    startStreaming: () => {
      setState("chat", {
        layoutState: ChatLayoutState.STREAMING,
        isStreamingActive: true,
      });
    },

    stopStreaming: () => {
      setState("chat", {
        layoutState: ChatLayoutState.ACTIVE,
        isStreamingActive: false,
      });
    },

    setChatError: () => {
      setState("chat", {
        layoutState: ChatLayoutState.ERROR,
        isStreamingActive: false,
      });
    },

    resetChat: () => {
      setState("chat", DEFAULT_STATE.chat);
    },

    incrementMessageCount: () => {
      setState("chat", "messageCount", state.chat.messageCount + 1);
    },

    // Extended pane actions
    setExtendedPaneMode: (mode: ExtendedPaneMode) => {
      setState("extendedPane", "mode", mode);
    },

    toggleExtendedPane: () => {
      const currentMode = state.extendedPane.mode;
      const newMode = currentMode === ExtendedPaneMode.HIDDEN ? ExtendedPaneMode.ACTIVE : ExtendedPaneMode.HIDDEN;
      setState("extendedPane", "mode", newMode);
    },

    setExtendedPanePosition: (position: "left" | "right") => {
      setState("extendedPane", "position", position);
    },

    setExtendedPaneWidth: (width: number, isManualResize = false) => {
      // Constraints: 10-70% of available space, min 300px for panel, ensure 420px remains for chat
      if (isServer) return;

      // Calculate space taken by navigation (extended pane is always on right side)
      const navWidth = state.navigationPane.mode === NavigationPaneMode.DOCKED ? state.navigationPane.width : 0;
      const availableWidth = window.innerWidth - navWidth;

      // Calculate constraints
      const minPanelWidth = 300;
      const minChatWidth = 420;
      const minWidth = Math.max(minPanelWidth, availableWidth * 0.1);
      const maxWidth = Math.min(availableWidth * 0.7, availableWidth - minChatWidth);

      // Ensure we have valid bounds
      const finalWidth = Math.max(minWidth, Math.min(maxWidth, width));
      setState("extendedPane", "width", finalWidth);

      // Mark as manually resized if this was a user action
      if (isManualResize) {
        setState("extendedPane", "hasBeenManuallyResized", true);
      }
    },

    setExtendedPaneContent: (content: JSXElement) => {
      setState("extendedPane", "content", content);
    },

    clearExtendedPaneContent: () => {
      setState("extendedPane", "content", undefined);
    },

    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    setExtendedPaneArtifact: (operationId: string, chat: any, title?: string, blockId?: string) => {
      const artifact: ArtifactData = {
        operationId,
        blockId,
        chat,
        title,
        type: ExtendedPaneContentType.ARTIFACT,
      };
      setState("extendedPane", "activeArtifact", artifact);
      setState("extendedPane", "content", undefined);
    },

    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    setExtendedPaneInterstitial: (operationId: string, chat: any, title?: string, blockId?: string) => {
      const artifact: ArtifactData = {
        operationId,
        blockId,
        chat,
        title,
        type: ExtendedPaneContentType.INTERSTITIAL,
      };
      setState("extendedPane", "activeArtifact", artifact);
      setState("extendedPane", "content", undefined);
    },

    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    setExtendedPaneInterstitialGenerating: (operationId: string, chat: any, title?: string, blockId?: string) => {
      const artifact: ArtifactData = {
        operationId,
        blockId,
        chat,
        title,
        type: ExtendedPaneContentType.INTERSTITIAL_GENERATING,
      };
      setState("extendedPane", "activeArtifact", artifact);
      setState("extendedPane", "content", undefined);
    },

    clearExtendedPaneArtifact: () => {
      setState("extendedPane", "activeArtifact", undefined);
    },

    // Prompt bar actions
    setPromptBarMode: (mode: PromptBarMode) => {
      setState("promptBar", "mode", mode);
    },

    setInteractionMode: (mode: InteractionMode) => {
      setState("promptBar", "interactionMode", mode);
    },

    toggleKnowledgeSource: (source: keyof PromptBarState["knowledgeSources"]) => {
      setState("promptBar", "knowledgeSources", source, !state.promptBar.knowledgeSources[source]);
    },

    resetPromptBar: () => {
      setState("promptBar", DEFAULT_STATE.promptBar);
    },

    // Debug actions
    toggleDebugMode: () => {
      setState("isDebugMode", !state.isDebugMode);
    },

    toggleRichTextReplacement: () => {
      setState("useRichTextReplacement", !state.useRichTextReplacement);
    },

    toggleAutoCollapseOperations: () => {
      setState("enableAutoCollapseOperations", !state.enableAutoCollapseOperations);
    },

    openSecondaryPane: (pane: "assets" | "concepts") => {
      setState("navigationPane", "secondaryPane", pane);
    },
    closeSecondaryPane: () => {
      setState("navigationPane", "secondaryPane", null);
    },
  };

  const contextValue: AppUIContextType = {
    state,
    isHydrated,
    actions,
  };

  return <AppUIContext.Provider value={contextValue}>{props.children}</AppUIContext.Provider>;
};

export const useAppUI = (): AppUIContextType => {
  const context = useContext(AppUIContext);
  if (!context) {
    throw new Error("useAppUI must be used within an AppUIContextProvider");
  }
  return context;
};
